import { useCallback, useMemo, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import {
  activateMarketAgency,
  deactivateAgency,
  deleteAgency,
  fetchAgencies,
} from "api/agencies"
import { columns, sortByDefault } from "./columns"
import { css, cx } from "@emotion/css"
import { defaultSort, limitAndOffset } from "api/utils"
import { queryCache, useMutation, useQuery } from "react-query"

import { ReactComponent as AgenciesIcon } from "assets/icon-companies.svg"
import { ApiError } from "api/http/utils"
import { EmptyState } from "components/EmptyState"
import { MarketAgencyState } from "models/agency"
import { Row } from "react-table"
import { SnackBar } from "components/SnackBar"
import { Table } from "components/tables"
import { history } from "my-history"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"

const styles = {
  container: css`
    text-align: right;
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
}

const getRowProps = (row: Row<MarketAgencyState>) => {
  const id = row.original.id
  return {
    onClick: () => history.push(`/market-agencies/${id}/profile`),
  }
}

type Props = {
  newAgency?: () => void
}

function MarketAgencies(props: Props) {
  const { t } = useI18n()
  const ls = useLocationSearch()
  const { hasAuthority } = useAuthenticated()
  const { newAgency } = props
  const [modalInfo, setModalInfo] = useState<ModalInfo>(undefined)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errors, setErrors] = useState<Record<string, any>>()

  const isModalOpen = Boolean(modalInfo)

  const params = ls
    .map(limitAndOffset)
    .map(defaultSort(sortByDefault))
    .toString()
  const { data, isLoading } = useQuery(
    ["market-agencies", params],
    fetchAgencies,
  )

  const rows = useMemo(() => {
    return data?.data ?? []
  }, [data])

  const [activate] = useMutation(activateMarketAgency, {
    onSuccess: async () => {
      setModalInfo(undefined)
      setShowSuccess(true)
      queryCache.invalidateQueries("market-agencies")
      history.push("/market-agencies")
    },
    onError: (e: any) => {
      const error = e as ApiError
      setErrors(error)
      setModalInfo(undefined)
      setShowError(true)
    },
  })

  const [deactivate] = useMutation(deactivateAgency, {
    onSuccess: async () => {
      setModalInfo(undefined)
      setShowSuccess(true)
      queryCache.invalidateQueries("market-agencies")
      history.push("/market-agencies")
    },
  })

  const [_delete] = useMutation(deleteAgency, {
    onSuccess: async () => {
      setModalInfo(undefined)
      setShowSuccess(true)
      queryCache.invalidateQueries("market-agencies")
      history.push("/market-agencies")
    },
  })

  const handleCloseSuccess = (_event?: any, reason?: string) => {
    if (reason === "clickaway") return
    setShowSuccess(false)
  }

  const handleCloseError = (_event?: any, reason?: string) => {
    if (reason === "clickaway") return
    setShowError(false)
  }

  const onActivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("agency.modal.activate.title"),
        description: t("agency.modal.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await activate({ id }),
      })
    },
    [activate, t],
  )

  const onDeactivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("agency.modal.deactivate.title"),
        type: "primary_destructive",
        description: t("agency.modal.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await deactivate(id),
      })
    },
    [deactivate, t],
  )

  const onDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("market-admin.agencies.list.delete.title"),
        type: "primary_destructive",
        description: t("market-admin.agencies.list.delete.description"),
        submitLabel: t("market-admin.agencies.list.delete.action"),
        onSubmit: async () => await _delete(id),
      })
    },
    [_delete, t],
  )

  const activateErrors =
    [] && errors && errors.fields.map((e: any) => `${e.name}: ${t(e.key)} \n`)

  return (
    <>
      <div className={styles.container}>
        <Table<MarketAgencyState>
          id="agencies-list-table"
          columns={columns(t, onActivate, onDeactivate, onDelete, hasAuthority)}
          data={rows}
          filters={[]}
          total={data?.total ?? 0}
          loading={isLoading}
          searchBy="search"
          getRowProps={getRowProps}
          noResults={
            <EmptyState
              title={t("agencies.noresults.title")}
              icon={<AgenciesIcon />}
              subtitle={
                hasAuthority(["AUTH_CREATE_AGENCY"])
                  ? t("agencies.noresults.subtitle")
                  : undefined
              }
            >
              {hasAuthority(["AUTH_CREATE_AGENCY"]) ? (
                <span
                  onClick={newAgency}
                  className={cx(
                    "BodyBody-1Primary-mainRegular",
                    styles.createNew,
                  )}
                >
                  {t("agencies.create_new")}
                </span>
              ) : null}
            </EmptyState>
          }
        />
      </div>
      <SnackBar
        open={showSuccess}
        severity="success"
        onClose={handleCloseSuccess}
        message={t("agency.save.success")}
      />
      <SnackBar
        open={showError}
        severity="error"
        onClose={handleCloseError}
        message={errors && activateErrors.join("")}
      />
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </>
  )
}

export default MarketAgencies
